import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, type FinishedRun } from '../api';
import { type TranslationKey, useT } from '../i18n';
import { MenuIcon } from './MenuIcon';

/**
 * "Agent X is done" notifications, top right, for the user's own agent and AI task runs that
 * finish while the app is open. One poll per source, every 25 s while the tab is visible and
 * on focus. The cursor is the server's time, remembered per user in localStorage, so a fresh
 * page does not replay old runs and a run is announced once.
 */
const POLL_MS = 25_000;
const DISMISS_MS = 8_000;
const MAX_VISIBLE = 3;

export type RunSource = 'agents' | 'automations';

export type RunNotice = FinishedRun & { source: RunSource };

export function cursorKey(userId: string, source: RunSource): string {
  return `nivio-chat:finished-runs:${userId}:${source}`;
}

function readCursor(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeCursor(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Private mode or blocked storage: the cursor stays in memory for this page.
  }
}

/** Where the notification links to: the run's result chat, else the run list. */
export function runLink(run: RunNotice): string {
  if (run.conversationId) return `/c/${run.conversationId}`;
  return run.source === 'agents' ? '/agents?tab=runs' : '/automations';
}

function useFinishedRuns(userId: string, source: RunSource, enabled: boolean) {
  const key = cursorKey(userId, source);
  const memory = useRef<string | null>(null);
  return useQuery({
    queryKey: ['finishedRuns', userId, source],
    queryFn: async (): Promise<RunNotice[]> => {
      const since = memory.current ?? readCursor(key);
      const result = await api.finishedRuns(source === 'agents', since);
      memory.current = result.now;
      writeCursor(key, result.now);
      return result.data.map((run) => ({ ...run, source }));
    },
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

export function RunNotifications({ userId, sources }: { userId: string; sources: RunSource[] }) {
  const agents = useFinishedRuns(userId, 'agents', sources.includes('agents'));
  const automations = useFinishedRuns(userId, 'automations', sources.includes('automations'));
  const [notices, setNotices] = useState<RunNotice[]>([]);
  const announced = useRef(new Set<string>());
  const location = useLocation();
  const pathname = useRef(location.pathname);
  pathname.current = location.pathname;
  const qc = useQueryClient();

  useEffect(() => {
    const fresh = [...(agents.data ?? []), ...(automations.data ?? [])].filter((run) => {
      if (announced.current.has(run.id)) return false;
      announced.current.add(run.id);
      return true;
    });
    if (fresh.length === 0) return;
    for (const source of new Set(fresh.map((run) => run.source))) {
      qc.invalidateQueries({ queryKey: [source] });
    }
    // The user is already looking at this result: no need to point at it.
    const shown = fresh.filter(
      (run) => !run.conversationId || pathname.current !== `/c/${run.conversationId}`,
    );
    if (shown.length === 0) return;
    setNotices((current) =>
      [...shown, ...current]
        .sort((a, b) => b.completedAt.localeCompare(a.completedAt))
        .slice(0, MAX_VISIBLE),
    );
  }, [agents.data, automations.data, qc]);

  function dismiss(id: string) {
    setNotices((current) => current.filter((notice) => notice.id !== id));
  }

  function open(notice: RunNotice) {
    dismiss(notice.id);
    if (notice.status !== 'completed') return;
    void api
      .markAutomationRunSeen(notice.id, notice.source === 'agents')
      .then(() => qc.invalidateQueries({ queryKey: [notice.source] }))
      .catch(() => undefined);
  }

  return (
    <div className="nv-toasts" role="status" aria-live="polite">
      {notices.map((notice) => (
        <RunToast
          key={notice.id}
          notice={notice}
          onDismiss={() => dismiss(notice.id)}
          onOpen={() => open(notice)}
        />
      ))}
    </div>
  );
}

const MESSAGE: Record<RunSource, Record<FinishedRun['status'], TranslationKey>> = {
  agents: { completed: 'runNotifyAgentDone', error: 'runNotifyAgentFailed' },
  automations: { completed: 'runNotifyTaskDone', error: 'runNotifyTaskFailed' },
};

function RunToast({
  notice,
  onDismiss,
  onOpen,
}: {
  notice: RunNotice;
  onDismiss: () => void;
  onOpen: () => void;
}) {
  const t = useT();
  const [paused, setPaused] = useState(false);
  const failed = notice.status === 'error';
  // The parent re-renders on every poll; keep the timer running across those renders.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (paused) return;
    const timer = window.setTimeout(() => dismissRef.current(), DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [paused]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover and focus only pause the timer
    <div
      className="nv-toast"
      data-tone={failed ? 'err' : 'ok'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false);
      }}
    >
      <div className="nv-toast-body">
        <span className={`nv-pill ${failed ? 'nv-err' : 'nv-ok'}`}>
          {t(failed ? 'runNotifyFailedLabel' : 'runNotifyDoneLabel')}
        </span>
        <p className="nv-toast-title">
          {t(MESSAGE[notice.source][notice.status]).replace('{name}', notice.title)}
        </p>
        <Link className="nv-btn2" to={runLink(notice)} onClick={onOpen}>
          {t('runNotifyOpen')}
        </Link>
      </div>
      <button
        type="button"
        className="nv-toast-close"
        aria-label={t('runNotifyDismiss')}
        onClick={onDismiss}
      >
        <MenuIcon name="close" />
      </button>
    </div>
  );
}
